// src/components/dashboard/RiskSummary.jsx
import React from 'react';
import { Box, Typography, Divider, CircularProgress } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const RiskSummary = ({ assessments }) => {
    if (!assessments || assessments.length === 0) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
                <Typography variant="body1" color="text.secondary" textAlign="center">
                    No assessments available.
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
                    Create an assessment to see your risk summary.
                </Typography>
            </Box>
        );
    }

    // Calculate risk level counts
    const highRiskCount = assessments.filter(a => a.risk_score >= 7).length;
    const mediumRiskCount = assessments.filter(a => a.risk_score >= 4 && a.risk_score < 7).length;
    const lowRiskCount = assessments.filter(a => a.risk_score < 4).length;

    // Data for pie chart
    const data = [
        { name: 'High Risk', value: highRiskCount, color: '#d32f2f' },
        { name: 'Medium Risk', value: mediumRiskCount, color: '#f57c00' },
        { name: 'Low Risk', value: lowRiskCount, color: '#388e3c' }
    ].filter(item => item.value > 0); // Only include non-zero values

    // Calculate average risk score
    const totalRiskScore = assessments.reduce((sum, assessment) => sum + assessment.risk_score, 0);
    const averageRiskScore = totalRiskScore / assessments.length;

    // Group assessments by domain
    const domainGroups = assessments.reduce((groups, assessment) => {
        const domain = assessment.domain;
        if (!groups[domain]) {
            groups[domain] = [];
        }
        groups[domain].push(assessment);
        return groups;
    }, {});

    // Calculate average risk score by domain
    const domainRiskScores = Object.entries(domainGroups).map(([domain, items]) => {
        const domainTotal = items.reduce((sum, item) => sum + item.risk_score, 0);
        const domainAverage = domainTotal / items.length;
        return {
            domain: domain.charAt(0).toUpperCase() + domain.slice(1),
            averageScore: domainAverage
        };
    });

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ height: 180 }}>
                {data.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={data}
                                cx="50%"
                                cy="50%"
                                innerRadius={30}
                                outerRadius={60}
                                paddingAngle={5}
                                dataKey="value"
                            >
                                {data.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip formatter={(value) => [`${value} assessment${value !== 1 ? 's' : ''}`, 'Count']} />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress size={40} />
                    </Box>
                )}
            </Box>

            <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                    Average Risk Score
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mt: 1,
                        mb: 2
                    }}
                >
                    <Box
                        sx={{
                            width: 64,
                            height: 64,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: getRiskColor(averageRiskScore),
                            color: 'white'
                        }}
                    >
                        <Typography variant="h5" fontWeight="bold">
                            {averageRiskScore.toFixed(1)}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Risk by Domain
                </Typography>
                {domainRiskScores.map((item, index) => (
                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">{item.domain}</Typography>
                        <Typography
                            variant="body2"
                            fontWeight="bold"
                            sx={{ color: getRiskColor(item.averageScore) }}
                        >
                            {item.averageScore.toFixed(1)}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

// Helper function to get color based on risk score
const getRiskColor = (score) => {
    if (score >= 7) return '#d32f2f'; // High risk - red
    if (score >= 4) return '#f57c00'; // Medium risk - orange
    return '#388e3c'; // Low risk - green
};

export default RiskSummary;